// @ts-nocheck
import type { ValidationResult } from '../../diagnostics';
import { addIssue, makePassResult, mergeResults } from '../../diagnostics';
import type { AnimationDefinition, AnimationTrack } from '../../types/animation';
import { validateAnimationTracks } from '../../runtime/animation/compileTracksToSteps';

const COLOR_PARAMS = new Set(['backgroundColor', 'borderColor', 'textColor', 'primaryColor', 'secondaryColor']);

function isValidHexColor(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{3}$/.test(normalized) || /^[0-9a-fA-F]{6}$/.test(normalized);
}

function validateTrackColors(track: AnimationTrack, path: string): ValidationResult {
  if (!COLOR_PARAMS.has(track.parameter)) {
    return makePassResult();
  }
  let result = makePassResult();
  for (let i = 0; i < track.values.length; i += 1) {
    const value = track.values[i];
    if (!isValidHexColor(value)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_animation_color',
        message: `Animation track '${track.parameter}' value at index ${i} is not a valid hex color`,
        path: `${path}.values[${i}]`,
      });
    }
  }
  return result;
}

export function validateAnimationDefinitions(
  animations: readonly AnimationDefinition[] | undefined,
): ValidationResult {
  if (!animations || animations.length === 0) {
    return makePassResult();
  }

  let result = makePassResult();
  const seenIds = new Set<string>();

  for (let i = 0; i < animations.length; i += 1) {
    const animation = animations[i];
    const basePath = `animations[${i}]`;

    if (seenIds.has(animation.id)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.duplicate_animation_id',
        message: `Duplicate animation id '${animation.id}'`,
        path: `${basePath}.id`,
      });
    } else {
      seenIds.add(animation.id);
    }

    if (animation.durationMs <= 0) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_animation_duration',
        message: `Animation '${animation.id}' durationMs must be positive`,
        path: `${basePath}.durationMs`,
      });
    }

    const trackError = validateAnimationTracks(animation.tracks);
    if (trackError !== null) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_animation_tracks',
        message: trackError,
        path: `${basePath}.tracks`,
      });
    }

    for (let t = 0; t < animation.tracks.length; t += 1) {
      result = mergeResults(
        result,
        validateTrackColors(animation.tracks[t], `${basePath}.tracks[${t}]`),
      );
    }
  }

  return result;
}
